import {toast} from 'react-toastify'
import {getApiErrorMessage, isApiPayloadSuccess} from 'src/marketConnect/helpers'
import type {AppDispatch} from './store'

const SKIP_GLOBAL_API_ERROR = Symbol('skipGlobalApiError')

/**
 * Opt a single thunk out of the global API error handling.
 *
 * When set, the wrapped dispatch will neither show the global error toast nor
 * reject the promise for non-2xx payloads — the caller receives the resolved
 * thunk action (fulfilled or rejected) and is responsible for handling it.
 * Use this for flows that intentionally inspect non-2xx responses inline
 * (e.g. auth/login/MFA where a 400 drives the next UI step).
 */
export function withSkipGlobalApiError<T extends object>(payload: T): T {
  return Object.assign({}, payload, {[SKIP_GLOBAL_API_ERROR]: true})
}

function shouldSkipGlobalApiError(action: unknown): boolean {
  const arg = (action as {meta?: {arg?: Record<PropertyKey, unknown>}})?.meta?.arg
  return arg?.[SKIP_GLOBAL_API_ERROR] === true
}

function isAsyncThunkResult(action: unknown): action is {meta: {requestStatus: string; aborted?: boolean}} {
  return Boolean(
    action &&
      typeof action === 'object' &&
      (action as {meta?: {requestStatus?: string}}).meta?.requestStatus
  )
}

/**
 * Wraps Redux dispatch so failed API thunks:
 * 1) reject the returned promise (.then success handlers are skipped)
 * 2) show the backend error message automatically
 */
export function wrapDispatchWithApiErrors(dispatch: AppDispatch): AppDispatch {
  const wrapped = ((action: unknown) => {
    const result = dispatch(action as Parameters<AppDispatch>[0])

    if (typeof action !== 'function') {
      return result
    }

    const promise = Promise.resolve(result).then((finalAction: unknown) => {
      if (!isAsyncThunkResult(finalAction)) {
        return finalAction
      }

      if (finalAction.meta.aborted) {
        return Promise.reject(finalAction)
      }

      if (!isApiPayloadSuccess(finalAction)) {
        // Caller opted out: hand back the raw action so it can handle the
        // non-2xx response inline (no global toast, no rejection).
        if (shouldSkipGlobalApiError(finalAction)) {
          return finalAction
        }
        toast.error(getApiErrorMessage(finalAction, 'Request failed'))
        return Promise.reject(finalAction)
      }

      return finalAction
    })

    // Preserve RTK thunk affordances so existing `dispatch(thunk).unwrap()`
    // (and `.abort`/`.requestId`/`.arg`) call sites keep working after wrapping.
    ;(promise as any).unwrap = () =>
      promise.then(
        (finalAction: any) => finalAction?.payload,
        (rejectedAction: any) => {
          if (rejectedAction?.meta?.rejectedWithValue) {
            throw rejectedAction.payload
          }
          throw rejectedAction?.error ?? rejectedAction
        }
      )

    if (result && typeof result === 'object') {
      const original = result as any
      if (typeof original.abort === 'function') {
        ;(promise as any).abort = original.abort.bind(original)
      }
      if ('requestId' in original) {
        ;(promise as any).requestId = original.requestId
      }
      if ('arg' in original) {
        ;(promise as any).arg = original.arg
      }
      if (typeof original.unsubscribe === 'function') {
        ;(promise as any).unsubscribe = original.unsubscribe.bind(original)
      }
    }

    return promise
  }) as AppDispatch

  return wrapped
}
